// Import the functions you need from the SDKs you need
import { initializeApp } from "firebase/app";
import {
  getAuth,
  RecaptchaVerifier,
  signInWithPhoneNumber,
  setPersistence,
  browserLocalPersistence,
} from "firebase/auth";
import { getFirestore } from "firebase/firestore";
// import { getAnalytics } from "firebase/analytics";
import { getDatabase } from "firebase/database";
import { getFunctions } from "firebase/functions";
import {
  writeData,
  readData,
  addData,
  updateData,
  deleteData,
  subscribeToData,
  queryData,
} from "./realtimeDb";

// Your web app's Firebase configuration
// For Firebase JS SDK v7.20.0 and later, measurementId is optional
const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  databaseURL: process.env.REACT_APP_FIREBASE_DATABASE_URL,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
  // measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID,
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);

// Initialize Firebase Authentication and get a reference to the service
export const auth = getAuth(app);

// Set language to English
auth.languageCode = "en";

// Set persistence to local to avoid popup issues
setPersistence(auth, browserLocalPersistence).catch((error) => {
  console.error("Error setting auth persistence:", error);
});

// Initialize Cloud Firestore and get a reference to the service
export const db = getFirestore(app);

// Initialize Realtime Database
export const realtimeDb = getDatabase(app);

// Initialize Analytics (disabled to prevent CSP issues)
// export const analytics = getAnalytics(app);

// Initialize Functions with region
export const functions = getFunctions(app, "us-central1");

let recaptchaVerifier = null;

// Function to initialize reCAPTCHA verifier
const initializeRecaptcha = () => {
  try {
    if (recaptchaVerifier) {
      try {
        recaptchaVerifier.clear();
      } catch (error) {
        console.error("Error clearing existing reCAPTCHA:", error);
      }
      recaptchaVerifier = null;
    }

    recaptchaVerifier = new RecaptchaVerifier(auth, "recaptcha-container", {
      size: "normal",
      callback: () => {
        console.log("reCAPTCHA verified");
      },
      "expired-callback": () => {
        console.log("reCAPTCHA expired");
        recaptchaVerifier = null;
      },
    });

    return recaptchaVerifier;
  } catch (error) {
    console.error("Error initializing reCAPTCHA:", error);
    throw error;
  }
};

// Function to send verification code
export const sendVerificationCode = async (phoneNumber) => {
  try {
    // Format phone number if needed
    const formattedPhone = phoneNumber.startsWith("+")
      ? phoneNumber
      : `+91${phoneNumber}`;

    // Initialize reCAPTCHA if not already initialized
    if (!recaptchaVerifier) {
      recaptchaVerifier = initializeRecaptcha();
      // Render the reCAPTCHA widget explicitly
      await recaptchaVerifier.render();
    }

    // Send verification code
    const confirmationResult = await signInWithPhoneNumber(
      auth,
      formattedPhone,
      recaptchaVerifier
    );
    window.confirmationResult = confirmationResult;
    return confirmationResult;
  } catch (error) {
    console.error("Error sending verification code:", error);
    // Clear the verifier if there's an error
    if (recaptchaVerifier) {
      try {
        recaptchaVerifier.clear();
      } catch (clearError) {
        console.error("Error clearing reCAPTCHA:", clearError);
      }
      recaptchaVerifier = null;
    }
    throw error;
  }
};

// Function to verify code
export const verifyCode = async (code) => {
  try {
    if (!window.confirmationResult) {
      throw new Error("Please send verification code first");
    }
    const result = await window.confirmationResult.confirm(code);
    return result;
  } catch (error) {
    console.error("Error verifying code:", error);
    throw error;
  }
};

// Function to cleanup reCAPTCHA
export const cleanupRecaptcha = () => {
  if (recaptchaVerifier) {
    try {
      recaptchaVerifier.clear();
    } catch (error) {
      console.error("Error clearing reCAPTCHA:", error);
    }
    recaptchaVerifier = null;
  }
};

// Write data
export const writeDataToDb = async (path, data) => {
  try {
    await writeData(path, data);
  } catch (error) {
    throw error;
  }
};

// Read data
export const readDataFromDb = async (path) => {
  try {
    const data = await readData(path);
    return data;
  } catch (error) {
    throw error;
  }
};

// Add new data with auto-generated key
export const addDataToDb = async (path, data) => {
  try {
    const newKey = await addData(path, data);
    return newKey;
  } catch (error) {
    throw error;
  }
};

// Update specific fields
export const updateDataInDb = async (path, data) => {
  try {
    await updateData(path, data);
  } catch (error) {
    throw error;
  }
};

// Delete data
export const deleteDataFromDb = async (path) => {
  try {
    await deleteData(path);
  } catch (error) {
    throw error;
  }
};

// Subscribe to real-time updates
export const subscribeToDataInDb = (path, callback) => {
  return subscribeToData(path, callback);
};

// Query with filters
export const queryDataInDb = async (path, options) => {
  return await queryData(path, options);
};

export default app;

// Utility function to handle authentication fallbacks
export const handleAuthError = (error) => {
  console.error("Authentication error:", error);
  
  // Handle popup-related errors
  if (error.code === "auth/popup-closed-by-user") {
    return {
      type: "popup_closed",
      message: "Sign-in was cancelled. Please try again.",
      suggestion: "Click the sign-in button again to retry."
    };
  }
  
  if (error.code === "auth/popup-blocked") {
    return {
      type: "popup_blocked", 
      message: "Popup was blocked by your browser.",
      suggestion: "Please allow popups for this site and try again, or try using email/password sign-in instead."
    };
  }
  
  if (error.code === "auth/cancelled-popup-request") {
    return {
      type: "popup_cancelled",
      message: "Sign-in was cancelled.",
      suggestion: "Please try again or use email/password sign-in."
    };
  }
  
  if (error.code === "auth/account-exists-with-different-credential") {
    return {
      type: "account_exists",
      message: "An account already exists with this email using a different sign-in method.",
      suggestion: "Please use the original sign-in method for this account."
    };
  }
  
  // Default error handling
  return {
    type: "unknown",
    message: "An error occurred during sign-in. Please try again.",
    suggestion: "Please check your internet connection and try again."
  };
};
